/**
 * @fileoverview General purpose utility functions used across the codebase.
 * Includes formatting, DOM manipulation, and map helper functions.
 * @module utils/jrfuncs
 */

import { doProjectTransformGeoData } from '../utils/jrgeofuncs.js';
import { DEBUG_CBM, MAPAPP_DEFAULTS, DOM_ELEMENTS } from '../constants.js';

const { ID_STATUS_TOAST_CONTAINER } = DOM_ELEMENTS;





// ==========================================
// UI / BOOTSTRAP HELPERS
// ==========================================


/**
 * Programmatically switches to a Bootstrap tab by element ID.
 * @param {string} tabElementId - The ID of the tab button element
 */
export function switchToBootstrapTabById(tabElementId) {
    const bsTabButton = document.getElementById(tabElementId);
    if (bsTabButton) {
        // Use Bootstrap's getOrCreateInstance to avoid creating duplicate Tab instances
        // which can cause the tab content panes to stop switching properly
        const tab = bootstrap.Tab.getOrCreateInstance(bsTabButton);
        tab.show();
    }
}



/**
 * Restarts a CSS fade animation on an element by removing and re-adding the animate class.
 * Forces a reflow to ensure the animation restarts properly.
 * @param {HTMLElement} el - The element to animate
 */
export function restartCssFadeAnimation(el) {

  // Remove animation class
  el.classList.remove('animate');

  // Force reflow to reset animation
  void el.offsetWidth;

  // Re-add animation class
  el.classList.add('animate');
}


/**
 * Removes the CSS fade animation class from an element.
 * @param {HTMLElement} el - The element to remove animation from
 */
export function removeCssFadeAnimation(el) {
  el.classList.remove('animate');
}





/**
 * Updates element visibility by toggling 'visible' class based on value.
 * Used for showing/hiding clear buttons when input has content.
 * @param {HTMLElement} el - The element to show/hide
 * @param {string} inputValue - The value to check (shows element if non-empty)
 */
export function updateElementVisibilityBasedOnValue(el, inputValue) {
    if (!el) return;

    if (inputValue && inputValue.trim() !== '') {
        el.classList.add('visible');
    } else {
        el.classList.remove('visible');
    }
}



/**
 * Updates element visibility based on an input element's current value.
 * Wrapper around updateElementVisibilityBasedOnValue that reads from input.
 * @param {HTMLElement} el - The element to show/hide
 * @param {HTMLInputElement} inputEl - The input element to read value from
 */
export function updateElementVisibilityBasedOnInputElementValue(el, inputEl) {
    const inputValue = inputEl.value;
    updateElementVisibilityBasedOnValue(el, inputValue);
}


/**
 * Sets up responsive text on an element that automatically switches between
 * a long and short string based on whether the long text would wrap.
 * Uses a ResizeObserver on the element's parent to re-evaluate on resize.
 *
 * @param {string} elementId - The ID of the DOM element to manage
 * @param {string} longText - The full-length text to display when it fits
 * @param {string} shortText - The abbreviated text to display when longText wraps
 * @returns {ResizeObserver|null} The observer (call .disconnect() to clean up), or null if element not found
 */
export function setupResponsiveText(elementId, longText, shortText) {
    const el = document.getElementById(elementId);
    if (!el) {
        return null;
    }

    if (!shortText || longText === shortText) {
        el.textContent = longText;
        return null;
    }

    const checkFit = () => {
        // temporarily prevent visual wrapping while we measure
        el.style.overflow = "hidden";
        el.textContent = longText;
        const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
        if (el.scrollHeight > lineHeight * 1.5) {
            el.textContent = shortText;
        }
        el.style.overflow = "";
    };

    const observer = new ResizeObserver(checkFit);
    observer.observe(el.parentElement);

    checkFit();
    return observer;
}




// ==========================================
// GENERAL HELPERS
// ==========================================


/**
 * Creates a standardized return value object with results, message, and success flag.
 * Useful for functions that need to return both data and status information.
 * @param {*} results - The results data (can be any type, often an array or null)
 * @param {string} diagnosticsMessage - Message describing the result or any errors
 * @param {boolean} success - Whether the operation succeeded
 * @returns {Object} Standardized object with {results, diagnosticsMessage, success}
 */
export function createAnnotatedReturnVal(results, diagnosticsMessage, success) {
    return { results, diagnosticsMessage, success };
}




// ==========================================
// UI TOASTS/STATUS
// ==========================================



let delayedMessageTimerId = null;

/**
 * Queues a delayed search warning message to display as a toast.
 * @param {string} message - The warning message to display
 */
export function queueDelayedSearchWarning(message) {
    queueDelayedMessage(message);
}


/**
 * Queues a message to display as a toast after a configurable delay.
 * Clears any previously queued message before scheduling the new one.
 * @param {string} message - The message to display after delay
 */
export function queueDelayedMessage(message) {
    // Clear any existing queued message first
    clearQueuedDelayedMessages();
    // Set a delayed timer call to show the error message
    delayedMessageTimerId = setTimeout(() => {
        if (DEBUG_CBM) {
            console.debug(`DEBUG: Toasting delayed message: ${message}.`)
        }
        showStatusError(message);
        delayedMessageTimerId = null;
    }, MAPAPP_DEFAULTS.DELAYED_MESSAGE_TIMEOUT_MS);
}

/**
 * Clears any queued delayed messages by stopping the pending timer.
 */
export function clearQueuedDelayedMessages() {
    // Clear any queued delayed messages by stopping the timer
    if (delayedMessageTimerId !== null) {
        clearTimeout(delayedMessageTimerId);
        delayedMessageTimerId = null;
    }
}

/**
 * Clears any queued delayed search messages.
 * Convenience wrapper around clearQueuedDelayedMessages.
 */
export function clearQueuedDelayedSearchMessages() {
    clearQueuedDelayedMessages();
}


// The status toasts below take PLAIN TEXT: the message is shown as text (newlines become line
// breaks), so data in it -- a lead id, a pdf title, an imported label -- can never become markup.
// A caller that really wants markup passes { trustedHtml: true } in the options and escapes
// (escapeHtml) every piece of data it interpolates.

/**
 * Shows an error message as a toast at the bottom of the screen.
 * The toast fades out and hides automatically after a few seconds.
 * @param {string} errorMessage - The error message to display (plain text)
 * @param {Object} [options] - Toast options (see showToastMessage)
 */
export function showStatusError(errorMessage, options = {}) {
    const outerClass = 'text-bg-danger';
    const innerClass = 'bi bi-exclamation-triangle-fill';
    showStatusMessage(errorMessage, outerClass, innerClass, MAPAPP_DEFAULTS.STATUS_TOAST_ERROR_DURATION_MS, options);
    }


/**
 * Shows an error message as a toast at the bottom of the screen.
 * The toast fades out and hides automatically after a few seconds.
 * @param {string} errorMessage - The error message to display (plain text)
 * @param {number} [overrideTimeMs=0] - Display duration in ms (0 = the default)
 * @param {Object} [options] - Toast options (see showToastMessage)
 */
export function showStatusWarning(errorMessage, overrideTimeMs=0, options = {}) {
    const outerClass = 'text-bg-warning';
    const innerClass = '';
    const timeMs = overrideTimeMs >0 ? overrideTimeMs : MAPAPP_DEFAULTS.STATUS_TOAST_WARNING_DURATION_MS;
    showStatusMessage(errorMessage, outerClass, innerClass, timeMs, options);
    }

/**
 * Shows a message as a toast at the bottom of the screen.
 * The toast fades out and hides automatically after a few seconds.
 * @param {string} message - The  message to display (plain text)
 * @param {number} [overrideTimeMs=0] - Display duration in ms (0 = the default)
 * @param {Object} [options] - Toast options (see showToastMessage)
 */
export function showStatusHelpful(message, overrideTimeMs=0, options = {}) {
    const outerClass = 'text-bg-success';
    const innerClass = '';
    const timeMs = overrideTimeMs >0 ? overrideTimeMs : MAPAPP_DEFAULTS.STATUS_TOAST_WARNING_DURATION_MS;
    showStatusMessage(message, outerClass, innerClass, timeMs, options);
    }

/**
 * Shows a status message as a Bootstrap toast notification.
 * @param {string} message - The message to display
 * @param {string} outerClass - CSS class for the toast container (e.g., 'text-bg-danger')
 * @param {string} innerClass - CSS class for the inner icon (e.g., 'bi bi-exclamation-triangle-fill')
 */
export function showStatusMessage(message, outerClass, innerClass, duration = MAPAPP_DEFAULTS.STATUS_TOAST_DEFAULT_DURATION_MS, options = {}) {
    showToastMessage(message, outerClass, innerClass, duration, options);
}


/**
 * Creates and displays a Bootstrap toast notification at the bottom of the screen.
 * The message is PLAIN TEXT by default (set as text, newlines shown as line breaks), so any data
 * in it is inert. Toast auto-hides after configured duration and removes itself from the DOM when hidden.
 * @param {string} message - The message to display (may contain newlines)
 * @param {string} outerClass - CSS class for the toast element (e.g., 'text-bg-danger'; a code constant)
 * @param {string} innerClass - CSS class for the inner icon element (a code constant)
 * @param {number} duration - Display duration in ms
 * @param {Object} [options]
 * @param {boolean} [options.trustedHtml=false] - Opt-in: the message is markup built by the caller,
 *   which must have escaped (escapeHtml) every piece of data in it; newlines still become line breaks
 */
export function showToastMessage(message, outerClass, innerClass, duration, options = {}) {
    const { trustedHtml = false } = options;
    const messageText = (message === null || message === undefined) ? '' : String(message);

    // Create or reuse toast container
    let toastContainer = document.getElementById(ID_STATUS_TOAST_CONTAINER);
    if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.id = ID_STATUS_TOAST_CONTAINER;
        //toastContainer.className = 'toast-container position-fixed bottom-0 start-50 translate-middle-x p-3';
        toastContainer.className = 'toast-container position-fixed top-50 start-50 translate-middle-x p-3';
        toastContainer.style.zIndex = '1100';
        document.body.appendChild(toastContainer);
    }

    // Create toast element
    const toastId = 'status-toast-' + Date.now();
    const toastHtml = `
        <div id="${toastId}" class="toast align-items-center ${outerClass} border-0" role="alert" aria-live="assertive" aria-atomic="true">
            <div class="d-flex">
                <div class="toast-body font-monospace">
                    <i class="${innerClass} me-2"></i><span class="status-toast-message"></span>
                </div>
                <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
            </div>
        </div>
    `;
    toastContainer.insertAdjacentHTML('beforeend', toastHtml);

    // Fill in the message: text by default, markup only when the caller opted in
    // (the element just appended, rather than a lookup by an id two toasts in one millisecond would share)
    const toastElement = toastContainer.lastElementChild;
    const messageEl = toastElement.querySelector('.status-toast-message');
    if (trustedHtml) {
        messageEl.innerHTML = messageText.replace(/\n/g, '<br/>');
    } else {
        appendTextWithLineBreaks(messageEl, messageText);
    }

    // Initialize and show the toast
    const toast = new bootstrap.Toast(toastElement, {
        autohide: true,
        delay: duration
    });
    toast.show();

    // Remove toast element from DOM after it's hidden
    toastElement.addEventListener('hidden.bs.toast', () => {
        toastElement.remove();
    });
}


/**
 * Appends text to an element as text nodes, turning each newline into a <br> element.
 * The text is never parsed as markup.
 * @param {HTMLElement} el - The element to append to
 * @param {string} text - The plain text
 */
export function appendTextWithLineBreaks(el, text) {
    const lines = String(text).split('\n');
    lines.forEach((line, index) => {
        if (index > 0) {
            el.appendChild(document.createElement('br'));
        }
        if (line) {
            el.appendChild(document.createTextNode(line));
        }
    });
}













// ==========================================
// FORMATTING HELPERS
// ==========================================



/**
* Pluralize a term based on count
* @param {number} count - The count to check
* @param {string} term - The term to pluralize (defaults to config.resultTerm)
* @returns {string} Pluralized string
*/
export function pluralize(count, term) {
    return `${term}${count !== 1 ? 's' : ''}`;
}



/**
 * Formats a timestamp as relative time (e.g., "2 hours ago").
 * Falls back to locale date string for dates older than a week.
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} Human-readable relative time string
 */
export function formatTimeAgo(timestamp) {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
    return new Date(timestamp).toLocaleDateString();
}





/**
 * Formats a byte size as a human-readable string (e.g., "500 bytes", "10.5k", "4.5mb").
 * @param {number} sizeInBytes - Size in bytes
 * @returns {string} Formatted size string
 */
export function formatSizeAsNiceSizeString(sizeInBytes) {
    if (sizeInBytes == null || sizeInBytes < 0) return '0 bytes';
    if (sizeInBytes < 1024) return `${sizeInBytes} bytes`;
    const kb = sizeInBytes / 1024;
    if (kb < 1024) return `${kb % 1 === 0 ? kb.toFixed(0) : kb.toFixed(1)}k`;
    const mb = kb / 1024;
    return `${mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1)}mb`;
}


/**
 * Similarity of two strings, 0..1, by the Dice coefficient of their character bigrams (white space
 * ignored) -- the algorithm of the string-similarity package's compareTwoStrings, which the advanced
 * search's fuzzy()/fuzzyCompare() called through a global the map page never loaded (so both always
 * failed with a ReferenceError). In-house so the map needs no third-party script for it.
 * @param {string} first
 * @param {string} second
 * @returns {number} 1 for identical strings, 0 for nothing in common
 */
export function compareTwoStringsDice(first, second) {
    first = String(first).replace(/\s+/g, '');
    second = String(second).replace(/\s+/g, '');
    if (first === second) return 1;
    if (first.length < 2 || second.length < 2) return 0;
    const firstBigrams = new Map();
    for (let i = 0; i < first.length - 1; i++) {
        const bigram = first.substring(i, i + 2);
        firstBigrams.set(bigram, (firstBigrams.get(bigram) || 0) + 1);
    }
    let intersection = 0;
    for (let i = 0; i < second.length - 1; i++) {
        const bigram = second.substring(i, i + 2);
        const count = firstBigrams.get(bigram) || 0;
        if (count > 0) {
            firstBigrams.set(bigram, count - 1);
            intersection++;
        }
    }
    return (2.0 * intersection) / (first.length + second.length - 2);
}


// Characters escaped by escapeHtml. Quotes are included so the result is safe inside a
// quoted attribute value too, not only in element text (the old textContent->innerHTML
// trick never escaped quotes, which let a value break out of value="...").
const HTML_ESCAPE_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * Escapes HTML special characters to prevent XSS.
 * THE one HTML escaper for the map app: safe both in element text and inside a quoted
 * attribute value (it escapes & < > " and ').
 * @param {*} text - Text to escape (null/undefined/false give ''; other values are stringified)
 * @param {boolean} [flagNewlinesToBrs] - If true, converts newlines to HTML line breaks
 * @returns {string} HTML-escaped text safe for innerHTML and attribute values
 */
export function escapeHtml(text, flagNewlinesToBrs) {
    if (text === null || text === undefined || text === false || text === '') return '';
    let htmlRetv = String(text).replace(/[&<>"']/g, (c) => HTML_ESCAPE_MAP[c]);
    if (flagNewlinesToBrs) {
        htmlRetv = htmlRetv.replaceAll('\n','\n<br/>');
    }
    return htmlRetv;
}


/**
 * Strips HTML tags from content for plain text extraction.
 * Parsed with DOMParser into a separate, INERT document: nothing in it loads or runs (no image
 * fetches, no handlers), so this is safe on any text. (It used to set innerHTML on an element of the
 * live page, which starts image loads and would run inline handlers wherever CSP allowed them.)
 * @param {string} html - HTML content to strip
 * @returns {string} Plain text content
 */
export function stripHtmlTags(html) {
    if (html === null || html === undefined || html === '') return '';
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    return (doc.body && doc.body.textContent) || '';
}


/**
 * Escapes special regex characters in a string.
 * @param {string} string - String to escape
 * @returns {string} Escaped string safe for use in RegExp
 */
export function escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}


// a word is a run of unicode letters/digits; anything else (whitespace, punctuation) separates words
const WORD_CHAR_REGEX = /[\p{L}\p{N}]/u;

/**
 * Tests whether text contains needle at the START of a word: at the start of the text,
 * or right after a non-word character (whitespace or punctuation).
 * So "smi" matches "john smith" and "o'smith" but not "blacksmith".
 * A needle that itself begins with a non-word character carries its own boundary, so it matches anywhere.
 * Case-sensitive: callers lowercase both sides.
 * @param {string} text - Text to search in
 * @param {string} needle - Substring that must begin a word of text
 * @returns {boolean} True if needle occurs at the start of some word in text
 */
export function includesAtStartOfWord(text, needle) {
    if (!needle || !WORD_CHAR_REGEX.test(needle[0])) {
        return text.includes(needle);
    }
    let pos = text.indexOf(needle);
    while (pos !== -1) {
        if (pos === 0 || !WORD_CHAR_REGEX.test(text[pos - 1])) {
            return true;
        }
        pos = text.indexOf(needle, pos + 1);
    }
    return false;
}


/**
 * includesAtStartOfWord, except that in the fields named in fullSearchFields the needle may match anywhere in the
 * text, not only at the start of a word.
 * @param {string} text - Text to search in (lowercased by the caller, like needle)
 * @param {string} needle - Substring to look for
 * @param {string} field - The name of the field text came from
 * @param {string[]} fullSearchFields - Fields searched anywhere rather than at word starts
 * @returns {boolean} True if needle occurs in text as the field's rule allows
 */
export function includesAtStartOfWordExceptFields(text, needle, field, fullSearchFields, startStringSearchFields) {
    if (fullSearchFields.includes(field)) {
        return text.includes(needle);
    }
    if (startStringSearchFields.includes(field)) {
        return text.startsWith(needle);
    }
    return includesAtStartOfWord(text, needle);
}


/**
 * Tests whether any of an item's text fields passes a test: each field's value, when it is a non-empty string, is
 * lowercased and handed to test(lowercasedValue, field). The one "does any field match" rule of the map's searches
 * (DbSearcher's two field searches, jrdslquery's substring test and the results filter box).
 * @param {Object} item - The item (a place, a bookmark ...)
 * @param {string[]} fields - The field names to try, in order
 * @param {Function} test - (lowercasedValue, fieldName) => boolean
 * @returns {boolean} True if the test passes for at least one field
 */
export function anyFieldMatches(item, fields, test) {
    return fields.some(field => {
        const value = item[field];
        return (!!value) && (typeof value === 'string') && (!!test(value.toLowerCase(), field));
    });
}




// ==========================================
// GENERIC UTILITY HELPERS
// ==========================================


/**
 * Generates a unique identifier string with optional prefix.
 * @param {string} [prefix=''] - Optional prefix for the ID
 * @returns {string} Unique identifier string
 */
export function generateUniqueId(prefix = '') {
    return `${prefix}${Math.random().toString(36).substr(2, 9)}`;
}


/**
 * Replaces the alpha channel of a hex color string with a new opacity value.
 * @param {string} colorCode - Hex color string, e.g. '#ffd700' or '#ffd7004d'
 * @param {number} opacity - Opacity value from 0 to 1
 * @returns {string} Hex color string with new alpha, e.g. '#ffd70033'
 */
export function replaceHtmlColorOpacity(colorCode, opacity) {
    return colorCode.slice(0, 7) + Math.round(opacity * 255).toString(16).padStart(2, '0');
}


/**
 * Turns a CSS colour into the one form `<input type="color">` takes, lowercase '#rrggbb' -- the
 * input silently turns anything else into black, so a pin imported as "red" or "#f00" went black
 * the first time its bookmark was edited and saved. Handles: hex of 3, 4, 6 or 8 digits (#rgb and
 * #rgba expanded); a colour name ("red", "steelblue"; resolved by the browser's own parser, so only
 * where there is a document); and `color(srgb r g b)` / `color(srgb r g b / a)`, which Safari 18.4+
 * reports for an `<input type="color" alpha>` -- such a value was once saved as a pin colour that
 * OpenLayers drew near-black and the import validator refused. Any other value (an unknown name, a
 * malformed string, a non-string) is returned unchanged, for the caller's own check. The ONE place
 * that repairs colours: used on the bookmark dialog's save, by the import / settings-restore field
 * check (which keeps only what comes out as '#rrggbb'), and where a custom pin's style is built (so
 * values stored earlier are drawn right). The map uses no colour transparency, so an alpha part is
 * dropped; color(srgb) channels are 0..1 numbers (or percentages), clamped to that range.
 * @param {*} value - A colour value
 * @returns {*} '#rrggbb' for a colour it can read, otherwise the value itself
 */
export function normalizeCssColorToHex(value) {
    if (typeof value !== 'string') {
        return value;
    }
    const text = value.trim();
    const hexMatch = text.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
    if (hexMatch) {
        const digits = hexMatch[1].toLowerCase();
        return (digits.length <= 4)
            ? '#' + digits.slice(0, 3).split('').map(digit => digit + digit).join('')
            : '#' + digits.slice(0, 6);
    }
    if (/^[a-z]{3,30}$/i.test(text)) {
        return resolveCssColorNameToHex(text) || value;
    }
    const match = text.match(/^color\(\s*srgb\s+([^\s/()]+)\s+([^\s/()]+)\s+([^\s/()]+)\s*(?:\/\s*[^\s/()]+\s*)?\)$/i);
    if (!match) {
        return value;
    }
    let hex = '#';
    for (const channelText of match.slice(1, 4)) {
        const channel = channelText.endsWith('%') ? Number(channelText.slice(0, -1)) / 100 : Number(channelText);
        if (!Number.isFinite(channel)) {
            // (e.g. the css keyword "none"): not a colour we can name, so leave it to the caller's check
            return value;
        }
        hex += Math.round(Math.min(1, Math.max(0, channel)) * 255).toString(16).padStart(2, '0');
    }
    return hex;
}


// a detached canvas's 2d context, made on first use (null where there is no document), and the
// names already resolved by it -- custom pin styles call normalizeCssColorToHex on every redraw
let cssColorProbeContext;
const cssColorNameHexCache = new Map();

/**
 * The '#rrggbb' of a CSS colour name, by a canvas fillStyle round trip (the browser's own colour
 * parser, so every name it knows, without a table here). An unknown name leaves fillStyle as it was,
 * so the name is set over two different starting colours and counts only when both agree; a colour
 * that is not opaque ("transparent") serializes as rgba(...) and gives null too.
 * @param {string} name - A colour name
 * @returns {string|null} '#rrggbb', or null for an unknown name or where there is no document
 * @private
 */
function resolveCssColorNameToHex(name) {
    const key = name.toLowerCase();
    if (cssColorNameHexCache.has(key)) {
        return cssColorNameHexCache.get(key);
    }
    let hex = null;
    try {
        if (cssColorProbeContext === undefined) {
            cssColorProbeContext = (typeof document !== 'undefined') ? document.createElement('canvas').getContext('2d') : null;
        }
        if (cssColorProbeContext) {
            const results = ['#000000', '#ffffff'].map(startColor => {
                cssColorProbeContext.fillStyle = startColor;
                cssColorProbeContext.fillStyle = key;
                return String(cssColorProbeContext.fillStyle).toLowerCase();
            });
            if ((results[0] === results[1]) && /^#[0-9a-f]{6}$/.test(results[0])) {
                hex = results[0];
            }
        }
    } catch (error) {
        hex = null;
    }
    cssColorNameHexCache.set(key, hex);
    return hex;
}


/**
 * Extracts the directory path from a file path by removing the filename.
 * @param {string} path - Full file path (e.g., '/dir/subdir/file.js')
 * @returns {string} Directory path without trailing filename (e.g., '/dir/subdir')
 */
export function jrPathtoDirectoryOfFile(path) {
  return path.replace(/\/[^/]*$/, "");
}


// Any absolute base does for urlPathname: only the PATH of the result is kept, so a relative url
// ("song.mp3", "/media/x.pdf") and an absolute one are read the same way.
const URL_PATH_PARSE_BASE = 'http://x/';

/**
 * The pathname of an absolute or relative url, without its query and fragment, as the URL parser
 * gives it (percent-encoded). Text the URL parser refuses is cut at its first '?' or '#'.
 * @param {string} url
 * @returns {string}
 * @private
 */
function urlPathname(url) {
    try {
        return new URL(url, URL_PATH_PARSE_BASE).pathname;
    } catch (e) {
        return url.split('?')[0].split('#')[0];
    }
}

/**
 * The file name of a url: the last segment of its PATHNAME, so a query or fragment (even one holding
 * a '/' or a '.ext') never counts. THE one way the map takes a url's file name (pair: urlExtension).
 * @param {string} url - An absolute or relative url
 * @param {Object} [options]
 * @param {boolean} [options.decode=false] - Percent-decode it for display ("My%20Case.pdf" ->
 *   "My Case.pdf"); a malformed escape keeps the encoded name rather than throwing
 * @returns {string} The file name, or '' when the path ends in '/' or there is no url
 */
export function urlFileName(url, { decode = false } = {}) {
    if (!url || typeof url !== 'string') return '';
    const pathname = urlPathname(url);
    const fileName = pathname.substring(pathname.lastIndexOf('/') + 1);
    if (!decode) return fileName;
    try {
        return decodeURIComponent(fileName);
    } catch (e) {
        return fileName;
    }
}

/**
 * The lowercased extension of a plain file name ("Song.MP3" -> "mp3"): letters and digits after the
 * last dot, '' when there are none. For a local File use this on file.name -- never urlExtension, which
 * would read a '#' or '?' in the name as the start of a fragment or query.
 * @param {string} fileName
 * @returns {string}
 */
export function fileNameExtension(fileName) {
    const m = String(fileName || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
}

/**
 * The lowercased extension of a url's file name (see urlFileName), ignoring its query and fragment:
 * "https://h/a/Song.MP3?v=2#t" -> "mp3", "/x?f=a.mp3" -> "". THE one way the map decides a url's kind
 * (audio, video, image, playlist) -- unwrap a /cases/mviewer/ link with PdfViewer.resolveMviewerFileUrl
 * first, since its file is in the query.
 * @param {string} url - An absolute or relative url
 * @returns {string} The extension without its dot, or ''
 */
export function urlExtension(url) {
    return fileNameExtension(urlFileName(url));
}


/**
 * Creates a shuffled copy of an array using Fisher-Yates algorithm.
 * Does not modify the original array.
 * @param {Array} arr - The array to shuffle
 * @returns {Array} A new array with elements in random order
 */
export function shuffledCopy(arr) {
  const copy = arr.slice(); // or [...arr]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}


/**
 * Returns a deep clone of a value. Uses structuredClone, which handles plain
 * objects, arrays, Date, Map, Set, and typed arrays correctly (unlike
 * JSON.parse/stringify, which silently drops undefined / functions and
 * mishandles non-JSON types).
 * @template T
 * @param {T} value - The value to deep-copy
 * @returns {T} A structurally-independent copy
 */
export function deepCopy(value) {
    return structuredClone(value);
}

/**
 * Replaces template placeholders in text with values from a dictionary.
 * Placeholders use ${key} syntax (e.g., "Hello ${name}").
 * @param {string} text - Text containing ${key} placeholders
 * @param {Object} keyVals - Dictionary of key-value pairs for substitution
 * @returns {string} Text with placeholders replaced by values
 */
export function replaceKeyValuesInText(text, keyVals) {
    if (!text || !keyVals) return text;
    let result = text;
    for (const [key, value] of Object.entries(keyVals)) {
        result = result.replaceAll(`\${${key}}`, value);
    }
    return result;
}

/**
 * Parses query string directives from the end of a search query.
 * Directives use the format: ;$name or ;$name=value or ;$name="quoted value"
 * @param {string} qstring - The query string potentially containing directives
 * @returns {Object} Object containing:
 *   - qstringNew: Query string with directives stripped
 *   - directiveDictionary: Object mapping directive names to values
 *   - errorString: Error message if parsing failed, null otherwise
 * @example
 * parseStringGetDirectiveDictionary("search term ;$limit=10 ;$random")
 * // Returns: { qstringNew: "search term", directiveDictionary: { limit: "10", random: undefined }, errorString: null }
 */
export function parseStringGetDirectiveDictionary(qstring) {
    // Special directives match the pattern ;$flag or ;$flag=val or ;$flag="quoted val" at end of qstring
    let errorString = null;

    let qstringNew = qstring.trim();
    // Regex to match directive at end of string
    // new trying to catch end stuff that is not quite right but which we want to give a directive error about later
    //const regexDirective = /;\s*\$([A-Za-z_]\w*)(?:\s*=\s*(?:"([^"]*)"|([^\s;]+)))?\s*$/i;
    const regexDirective = /;\s*\$([A-Za-z_]\w*)(?:\s*=\s*(?:"([^"]*)"|([^\s;]*)))?[\s;]*$/i;

    // Loop to strip off directives from end of qstring (right to left)
    const directiveDictionary = {};
    let match;
    while ((match = qstringNew.match(regexDirective)) !== null) {
        const name = match[1].toLowerCase();
        // Value is either quoted (group 2) or unquoted (group 3), or undefined if no value
        const value = match[2] !== undefined ? match[2] : match[3];

        // Check for duplicate directive
        if (name in directiveDictionary) {
            if (errorString) {
                errorString += '. ';
            } else {
                errorString = '';
            }
            errorString += `Duplicate directive: $${name}`;
        }

        // Store it (later occurrence overwrites earlier)
        directiveDictionary[name] = value;

        // Strip the matched directive from the qstring
        qstringNew = qstringNew.slice(0, match.index).trim();
    }

    // return new qstring and directives dictionary
    return {qstringNew, directiveDictionary, errorString};
}



// ==========================================
// CACHE BUSTER HELPERS
// ==========================================

/**
 * Appends a cache buster query parameter to a URL.
 * Handles URLs with existing query strings.
 * @param {string} url - The URL to modify
 * @param {string|null} version - Cache buster version string
 * @returns {string} URL with cache buster parameter appended, or original URL if no version
 */
export function appendCacheBuster(url, version) {
    if (!version) {
        return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}cachev=${version}`;
}

/**
 * Strips the query string from a URL.
 * @param {string} url - The URL to modify
 * @returns {string} URL without query string
 */
export function stripQueryString(url) {
    const queryIndex = url.indexOf('?');
    return queryIndex === -1 ? url : url.substring(0, queryIndex);
}




// ==========================================
// TRUSTED APP-DATA LOCATIONS
// ==========================================
// The JSON / HTML the app parses (app config, world configs, POI and aux data, icon maps, help) may
// come only from the app's own folders: its static base and the world-data tree (/media/cbm/ in
// Django). "Same origin" is NOT enough -- case authors' uploads are served from the same origin
// (/media/cases/...), so a link could otherwise make the app parse an uploaded file as its config or
// help. main.js sets the roots once at startup; loadJsonDataFromPath and the help loader enforce them.
// Tile images are deliberately not covered.

let trustedAppDataRoots = [];

/**
 * Resolves a path against the page url and returns it as a directory url (trailing slash, no
 * query string or fragment). An empty string means the page's own directory.
 * @param {string} path - Directory path (relative, root-relative or absolute)
 * @returns {URL|null} The directory url, or null if the path cannot be parsed
 */
export function resolveDirectoryUrl(path) {
    let resolved;
    try {
        resolved = new URL((path === '' || path === null || path === undefined) ? './' : String(path), window.location.href);
    } catch (e) {
        return null;
    }
    resolved.search = '';
    resolved.hash = '';
    if (!resolved.pathname.endsWith('/')) {
        resolved.pathname += '/';
    }
    return resolved;
}

/**
 * Sets the folders the app may load its parsed data from (see the section comment above).
 * @param {Array<string|null>} rootPaths - Directory paths; null/undefined entries are skipped
 */
export function setTrustedAppDataRoots(rootPaths) {
    trustedAppDataRoots = [];
    for (const rootPath of rootPaths || []) {
        if (rootPath === null || rootPath === undefined) continue;
        const dirUrl = resolveDirectoryUrl(rootPath);
        if (dirUrl && !trustedAppDataRoots.some(root => root.href === dirUrl.href)) {
            trustedAppDataRoots.push(dirUrl);
        }
    }
}

/**
 * @returns {string} The trusted roots as a readable list (for messages)
 */
export function describeTrustedAppDataRoots() {
    const pageOrigin = window.location.origin;
    return trustedAppDataRoots.map(root => (root.origin === pageOrigin) ? root.pathname : root.href).join(', ') || '(none configured)';
}

/**
 * Whether a url lies inside one of the trusted app-data roots.
 * The url is resolved against the page (so '..' segments are already applied); percent-encoded
 * slashes, backslashes and dots are refused outright, since a server may decode them into a path
 * that leaves the root after this check has passed.
 * @param {string} url - The url to test (relative or absolute)
 * @returns {boolean} True if it is inside a trusted root
 */
export function isTrustedAppDataUrl(url) {
    if (typeof url !== 'string' || !url || url.includes('\\')) {
        return false;
    }
    let resolved;
    try {
        resolved = new URL(url, window.location.href);
    } catch (e) {
        return false;
    }
    if (/%2f|%5c|%2e/i.test(resolved.pathname)) {
        return false;
    }
    return trustedAppDataRoots.some(root => resolved.origin === root.origin && resolved.pathname.startsWith(root.pathname));
}

/**
 * Throws unless the url lies inside a trusted app-data root (see isTrustedAppDataUrl).
 * @param {string} url - The url about to be fetched and parsed
 * @param {string} label - What is being loaded (for the error message)
 * @throws {Error} If the url is outside the trusted roots
 */
export function assertTrustedAppDataUrl(url, label) {
    if (!isTrustedAppDataUrl(url)) {
        throw new Error(`refused to load ${label || 'data'} from "${url}": the map only reads its data from ${describeTrustedAppDataRoots()}`);
    }
}

/**
 * Normalizes a static base path so files can be joined onto it as a PATH: resolved against the page,
 * query string and fragment dropped, trailing slash added (so a "?" or "#" in it can no longer turn
 * every "base + relative" url into the same file). '' (the page's own directory, standalone mode)
 * stays ''. A same-origin base becomes root-relative.
 * @param {string} basePath - The static base path
 * @returns {string} The normalized base path
 */
export function normalizeStaticBasePath(basePath) {
    if (!basePath) {
        return '';
    }
    const dirUrl = resolveDirectoryUrl(basePath);
    if (!dirUrl) {
        return '';
    }
    return (dirUrl.origin === window.location.origin) ? dirUrl.pathname : dirUrl.href;
}




// ==========================================
// FILE LOAD HELPERS
// ==========================================




/**
 * Resolves a relative child path using the directory of a parent file path.
 * @param {string} parentFilePath - Full file path of the parent (directory is extracted)
 * @param {string} childPath - Child path that may start with './' for relative resolution
 * @returns {string} Resolved path
 */
export function fixRelativeChildPathWithParentFilePath(parentFilePath, childPath) {
    return fixRelativeChildPathWithParentDirPath(jrPathtoDirectoryOfFile(parentFilePath), childPath);
}

/**
 * Resolves a relative child path using a parent directory path.
 * If childPath starts with './', it is resolved relative to parentPath.
 * Otherwise, childPath is returned unchanged.
 * ATTN: TODO this code is unnesc. confusing and messy, it's ridiculous. Please fix and standardize
 * @param {string} parentPath - Parent directory path
 * @param {string} childPath - Child path that may start with './' for relative resolution
 * @returns {string} Resolved path
 */
export function fixRelativeChildPathWithParentDirPath(parentPath, childPath) {
    //if (!parentPath.endsWith('/') && !parentPath.endsWith('\\')) {
    //    parentPath = parentPath + '/';
    //}
    if (childPath.startsWith("./")) {
        return parentPath.replace(/\/+$/, "") + "/" + childPath.slice(2);
    }
    if (childPath.startsWith("../")) {
        let resolvedParent = parentPath.replace(/\/+$/, "");
        let remaining = childPath;
        while (remaining.startsWith("../")) {
            const stripped = resolvedParent.replace(/\/[^/]*$/, "");
            resolvedParent = (stripped === resolvedParent) ? "" : stripped;
            remaining = remaining.slice(3);
        }
        const retPath = resolvedParent + "/" + remaining;
        // console.log(`Resolving parent=${parentPath} and child=${childPath} to ${retPath}.`)
        return retPath
    }
    return childPath;
}



/**
 * Loads and parses JSON data from a URL path.
 * Supports both plain JSON and gzip-compressed (.gz) files.
 * Optionally simulates a network delay for debugging purposes.
 * @async
 * @param {string} path - URL path to fetch the JSON data from
 * @param {string} debugOperationLabel - Label for error messages describing the operation
 * @param {number} [debugSimulateNetworkDelayMs=0] - Milliseconds to delay for debug simulation
 * @param {Object} [fetchOptions] - Passed to fetch(), e.g. {cache: "no-cache"} for a file with no cache-buster in its url
 * @returns {Promise<Object>} Parsed JSON data
 * @throws {Error} If fetch fails or JSON parsing fails
 */
export async function loadJsonDataFromPath(path, debugOperationLabel, debugSimulateNetworkDelayMs, fetchOptions = undefined) {
    let jsonData;
    try {

        // ATTN: JR this is a kludgey workaround for www.nynoir.org url which can create CORS problems now so we change it to canonical non www
        path = path.replace("www.nynoir.org", "nynoir.org");

        // everything parsed here is app data (configs, POI/aux data, icon maps): only from the app's own folders
        assertTrustedAppDataUrl(path, debugOperationLabel);

        const response = await fetch(path, fetchOptions);
        if (!response.ok) {
            throw new Error(response.statusText);
        }

        // Handle gzipped files (check path without query string for .gz extension)
        const pathWithoutQuery = path.split('?')[0];
        if (pathWithoutQuery.endsWith('.gz')) {
            const contentEncoding = response.headers.get('Content-Encoding');
            const contentType = response.headers.get('Content-Type');
            if (DEBUG_CBM) {
                console.log(`CasebookMap loading ${path}; Content-Encoding: ${contentEncoding}, Content-Type: ${contentType}`);
            }
            // If Content-Encoding is already gzip, browser handled decompression, so just parse as JSON directly
            if (contentEncoding === 'gzip') {
                jsonData = await response.json();
            } else {
                const arrayBuffer = await response.arrayBuffer();
                const decompressedStream = new DecompressionStream('gzip');
                const decompressedResponse = new Response(
                    new Blob([arrayBuffer]).stream().pipeThrough(decompressedStream)
                );
                jsonData = await decompressedResponse.json();
            }
        } else {
            jsonData = await response.json();
        }
    } catch (error) {
        console.error(`ERROR: Failed to ${debugOperationLabel} from url path "${path}": ${error}`);
        throw error;
    }

    // simulate a network delay here
    if (debugSimulateNetworkDelayMs > 0) {
        console.warn(`Casebook Map... Debug simulating a delay of ${debugSimulateNetworkDelayMs}ms during file load of "${path}".. `);
        await new Promise(resolve => setTimeout(resolve, debugSimulateNetworkDelayMs));
        console.warn('Casebook Map...  delay complete.');
    }

    return jsonData;
}


/**
 * Loads plain text data from a URL path (e.g. an .m3u playlist file).
 * @async
 * @param {string} path - URL path to fetch the text data from
 * @param {string} debugOperationLabel - Label for error messages describing the operation
 * @returns {Promise<string>} The response body as text
 * @throws {Error} If the fetch fails
 */
export async function loadTextDataFromPath(path, debugOperationLabel) {
    try {
        // mirror the www->canonical workaround used by loadJsonDataFromPath to avoid CORS surprises
        path = path.replace("www.nynoir.org", "nynoir.org");
        const response = await fetch(path);
        if (!response.ok) {
            throw new Error(response.statusText);
        }
        return await response.text();
    } catch (error) {
        console.error(`ERROR: Failed to ${debugOperationLabel} from url path "${path}": ${error}`);
        throw error;
    }
}





/**
 * Creates an empty search result structure with default values.
 * @param {string|null} error - Optional error message to include
 * @returns {Object} Empty search result with results array, totalCount, truncated flag, and error
 */
export function buildEmptySearchResultStructure(error) {
    const resultStructure = { results: [], totalCount: 0, truncated: false, error: error };
    return resultStructure;
}




/**
 * Appends an error string to an object's error field.
 * If the object already has an error, the new error is concatenated with a semicolon separator.
 * @param {Object} dictStruct - Object with an optional error property
 * @param {string|null} error - Error message to add (no-op if falsy)
 * @returns {string|undefined} The full accumulated error string, or undefined if no error was added
 */
export function addErrorToStruct(dictStruct, error) {
    if (!error) {
        return;
    }
    if (!dictStruct.error) {
        dictStruct.error = error;
    } else {
        dictStruct.error += "; " + error;
    }
    // return full error
    return dictStruct.error;
}

/**
 * Concatenates a new error string onto an existing error string with a semicolon separator.
 * If errorExisting is falsy, returns errorNew directly.
 * @param {string|null} errorExisting - Existing error string (may be null)
 * @param {string} errorNew - New error string to append
 * @returns {string} Combined error string
 */
export function addErrorToString(errorExisting, errorNew) {
    if (!errorExisting) {
        return errorNew;
    }
    return errorExisting + '; ' + errorNew;
}





























// ==========================================
// HELPERS FOR SEARCH RESULTS AND SEARCHPACK
// ==========================================



/**
 * Creates an empty search pack object.
 * Search packs are used to pass search parameters and results between components.
 * @returns {Object} Empty object to be populated with search parameters
 */
export function buildEmptySearchPack() {
    return {};
}



/**
 * Updates a search pack with information from a search result.
 * Sets hasSearched flag, result counts, truncation status, and any errors.
 * @param {Object} searchPack - The search pack to update
 * @param {Object} searchResult - The search result containing resultCount, totalCount, and error
 */
export function updateSearchPackWithSearchResult(searchPack, searchResult) {
    searchPack.hasSearched = true;
    searchPack.resultCount = searchResult.resultCount;
    searchPack.totalCount = searchResult.totalCount;
    searchPack.truncated = searchResult.resultCount < searchResult.totalCount;
    searchPack.error = searchResult.error;
}


/**
 * Builds a standardized search result object from raw results.
 * Calculates resultCount from the results array and determines truncation status.
 * @param {Array} results - Array of result items to include
 * @param {number} totalCount - Total number of matching items (before truncation)
 * @param {string|null} error - Optional error message
 * @returns {Object} Search result with results, resultCount, totalCount, truncated, and error
 */
export function buildSearchResult(results, totalCount, error) {
    const searchResult = {
        results: results,
        resultCount: results.length,
        totalCount: totalCount,
        truncated: totalCount > results.length,
        error: error
    };
    return searchResult;
}


/**
 * Builds a search pack object from a query string and searcher name.
 * The search pack carries query, options, and metadata through the search pipeline.
 * @param {string} queryString - The search query string
 * @param {string} searcherName - Name of the searcher type (e.g., 'white', 'yellow')
 * @param {boolean} [isEnterKey=true] - Whether the search was triggered by Enter key
 * @param {boolean} [flagUserInitiatedThisSearch=true] - Whether user initated search (vs an auto re-run, etc.)
 * @returns {Object} Search pack with searchType, query, queryOriginal, isEnterKey, and flagUserInitiatedThisSearch
 */
export function buildSearchPackFromQuery(queryString, searcherName, isEnterKey, flagUserInitiatedThisSearch, flagFromMapClick) {
    const searchPack = {
        searchType: searcherName,
        query: queryString,
        queryOriginal: queryString,
        isEnterKey: isEnterKey,
        flagUserInitiatedThisSearch: flagUserInitiatedThisSearch,
        flagFromMapClick: flagFromMapClick,
    }
    return searchPack;
}


/**
 * Resets a search pack to its original query state for re-execution.
 * Restores the original query and clears result-related properties.
 * Used when re-running a search (e.g., "show all" or "re-randomize").
 * @param {Object} searchPack - The search pack to reset
 * @param {string} searchPack.queryOriginal - The original query string to restore
 */
export function resetSearchPackToOriginalBlankSearch(searchPack) {
    const queryOriginal = searchPack.queryOriginal;
    searchPack.query = queryOriginal;
    // set these values
    searchPack.waitingOnEnter = false;
    searchPack.isEnterKey = true;
    searchPack.flagUserInitiatedThisSearch = true;
    // reset/delete these
    delete searchPack.hasSearched;
    delete searchPack.waitingOnEnter;
    delete searchPack.resultCount;
    delete searchPack.totalCount;
    delete searchPack.truncated;
    delete searchPack.error;
}


/**
 * Creates a shallow copy of an item.
 * @param {Object} item - The item to copy
 * @returns {Object} A new object with all properties shallow-copied from the original
 */
export function createItemCopy(item) {
    const newItem = {
        ...item
    }
    return newItem;
}


/**
 * Looks a key up in a plain dictionary object as an OWN property only. A bare obj[key] with a key
 * that comes from stored, imported or url data finds the Object.prototype members too: "constructor",
 * "toString" or "__proto__" then resolve to a function or to the prototype itself instead of "not
 * there". Use this wherever such a key indexes a dictionary (world ids, searcher names, ...).
 * @param {Object} obj - The dictionary (anything that is not an object finds nothing)
 * @param {string} key - The key
 * @returns {*} The own value, or undefined if obj has no own property of that name
 */
export function getOwnValue(obj, key) {
    if (!obj || typeof obj !== 'object' || (typeof key !== 'string' && typeof key !== 'number')) {
        return undefined;
    }
    return Object.prototype.hasOwnProperty.call(obj, key) ? obj[key] : undefined;
}



/**
 * Compares two objects for equality, ignoring specified fields.
 * @param {Object} itemA - First item to compare
 * @param {Object} itemB - Second item to compare
 * @param {Array<string>} ignoreFields - Field names to exclude from comparison
 * @returns {boolean} True if items are equal (excluding ignored fields)
 */
export function compareItemsIgnoreFields(itemA, itemB, ignoreFields) {
    const ignoreSet = new Set(ignoreFields || []);

    const keysA = Object.keys(itemA).filter(k => !ignoreSet.has(k));
    const keysB = Object.keys(itemB).filter(k => !ignoreSet.has(k));

    if (keysA.length !== keysB.length) {
        return false;
    }

    for (const key of keysA) {
        if (!(key in itemB)) {
            return false;
        }
        if (itemA[key] !== itemB[key]) {
            //console.log(`compareItemsIgnoreFields differ in key ${key} A="${itemA[key]}" and B="${itemB[key]}".`)
            return false;
        }
    }

    return true;
}






let _splitDNameRegex = null;

/**
 * Splits a dName into a base name and suffix at the last '-' or '(' character.
 * @param {string} dName - The display name to split
 * @returns {{baseName: string, suffix: string}} Object with baseName and trimmed suffix
 */
export function splitDNameIntoBaseAndSuffix(customBase, dName) {
    if (customBase) {
        return {baseName: customBase, suffix: dName}
    }
    if (!dName) {
        return { baseName: '', suffix: '' };
    }

    if (!_splitDNameRegex) {
        // Match: everything up to the last " - suffix" or trailing "(suffix)"
        _splitDNameRegex = /^(.*?)(?:\s*-\s*([^-]+?)|\s*\(([^)]+)\))?\s*$/;
    }

    const match = dName.match(_splitDNameRegex);
    if (!match) {
        return { baseName: dName.trim(), suffix: '' };
    }

    const baseName = (match[1] || '').trim();
    const suffix = (match[2] || match[3] || '').trim();

    return { baseName: baseName || dName.trim(), suffix };
}





/**
 * Simple string hash function (djb2 algorithm).
 * @param {string} str - The string to hash
 * @returns {string} Hash as a hex string
 */
export function jrhash(str) {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    }
    return (hash >>> 0).toString(16);
}

